import React, { useState, useEffect } from 'react';
import './OperatorManager.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

const OperatorManager = () => {
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingOperator, setEditingOperator] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        employeeNumber: ''
    });

    useEffect(() => {
        fetchOperators();
    }, []);

    const fetchOperators = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/operators`);
            if (!response.ok) throw new Error('Failed to fetch operators');
            const data = await response.json();
            setOperators(data);
        } catch (err) {
            console.error('Error fetching operators:', err);
            setError('Failed to load operators');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            const url = editingOperator
                ? `${API_URL}/api/operators/${editingOperator.id}`
                : `${API_URL}/api/operators`;
            
            const method = editingOperator ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save operator');
            }

            await fetchOperators();
            handleCancel();
        } catch (err) {
            console.error('Error saving operator:', err);
            setError(err.message);
        }
    };

    const handleEdit = (operator) => {
        setEditingOperator(operator);
        setFormData({
            name: operator.name,
            employeeNumber: operator.employeeNumber || ''
        });
        setShowForm(true);
    };

    const handleDeactivate = async (id) => {
        if (!window.confirm('Are you sure you want to deactivate this operator?')) {
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/operators/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) throw new Error('Failed to deactivate operator');

            await fetchOperators();
        } catch (err) {
            console.error('Error deactivating operator:', err);
            setError('Failed to deactivate operator');
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingOperator(null);
        setFormData({ name: '', employeeNumber: '' });
        setError(null);
    };

    return (
        <div className="operator-manager">
            <div className="operator-header">
                <h1>Operator Management</h1>
                {!showForm && (
                    <button className="add-button" onClick={() => setShowForm(true)}>
                        + Add Operator
                    </button>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}

            {showForm && (
                <div className="operator-form-card">
                    <h2>{editingOperator ? 'Edit Operator' : 'Add New Operator'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Name *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Employee Number</label>
                            <input
                                type="text"
                                value={formData.employeeNumber}
                                onChange={(e) => setFormData({ ...formData, employeeNumber: e.target.value })}
                            />
                        </div>
                        <div className="form-actions">
                            <button type="submit" className="save-button">
                                {editingOperator ? 'Update' : 'Create'}
                            </button>
                            <button type="button" className="cancel-button" onClick={handleCancel}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div className="loading">Loading operators...</div>
            ) : (
                <div className="operators-table-container">
                    <table className="operators-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Employee Number</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {operators.map(operator => (
                                <tr key={operator.id} className={!operator.active ? 'inactive' : ''}>
                                    <td className="operator-name">{operator.name}</td>
                                    <td>{operator.employeeNumber || '-'}</td>
                                    <td>
                                        <span className={`status-badge ${operator.active ? 'active' : 'inactive'}`}>
                                            {operator.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>{new Date(operator.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        {operator.active && (
                                            <>
                                                <button
                                                    className="edit-btn"
                                                    onClick={() => handleEdit(operator)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="deactivate-btn"
                                                    onClick={() => handleDeactivate(operator.id)}
                                                >
                                                    Deactivate
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {operators.length === 0 && (
                        <div className="no-data">No operators found. Click "Add Operator" to create one.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OperatorManager;
